import type { FuelKind } from "./catalogs";
import type { AircraftSpec, FuelUnit } from "./types";

export type { FuelUnit };

/** Catalog / ledger prices are US gallon. Imperial gallon is not used. Desk figures default to pounds. */
export const DEFAULT_FUEL_UNIT: FuelUnit = "lb";

export const FUEL_UNITS: FuelUnit[] = ["lb", "kg", "gal", "L"];

export const FUEL_UNIT_LABEL: Record<FuelUnit, string> = {
  lb: "lb",
  kg: "kg",
  gal: "US gal",
  L: "L",
};

/**
 * Conversion constants — explicit and testable. Do not invent ad-hoc densities.
 *
 * - `gal` is the US gallon (NIST 3.785411784 L). Not Imperial.
 * - Catalog `pricePerGal` is always US gal.
 * - Mass↔volume uses common ops-planning densities (not temperature-corrected):
 *   Jet-A  6.7 lb / US gal  (≈ 0.803 kg/L)
 *   Avgas  6.0 lb / US gal
 * - Mass: 1 lb = 0.45359237 kg (exact SI).
 */
export const US_GAL_LITERS = 3.785411784;
export const KG_PER_LB = 0.45359237;
export const JET_A_LB_PER_US_GAL = 6.7;
export const AVGAS_LB_PER_US_GAL = 6.0;

const MASS: ReadonlySet<FuelUnit> = new Set(["lb", "kg"]);
const VOLUME: ReadonlySet<FuelUnit> = new Set(["gal", "L"]);

export function isFuelUnit(value: unknown): value is FuelUnit {
  return value === "lb" || value === "kg" || value === "gal" || value === "L";
}

export function hydrateFuelUnit(raw?: unknown, fallback: FuelUnit = DEFAULT_FUEL_UNIT): FuelUnit {
  return isFuelUnit(raw) ? raw : fallback;
}

/**
 * The Hawker template used to stamp `fuelUnit: "kg"` on every copy.
 * That stamp is not a person choosing kilograms. A Settings or editor change sets `fuelUnitChosen`.
 * Copies keep their own id (a renamed tail such as N707BM) and the H25B type.
 */
export function isLegacySilentFuelUnit(
  aircraft?: Pick<AircraftSpec, "id" | "icaoType" | "fuelUnit" | "fuelUnitChosen"> | null,
): boolean {
  if (!aircraft || aircraft.fuelUnitChosen === true) return false;
  if (aircraft.fuelUnit !== "kg") return false;
  if (aircraft.id === "hawker-800xp") return true;
  return (aircraft.icaoType ?? "").trim().toUpperCase() === "H25B";
}

/**
 * Display and entry unit. An explicit per-tail choice wins, then the company
 * setting. A tail with no choice, and a Hawker that only has the old kilogram
 * stamp, shows pounds. Stored quantities are not rewritten.
 */
export function resolveAircraftFuelUnit(
  aircraft?: Pick<AircraftSpec, "id" | "icaoType" | "fuelUnit" | "fuelUnitChosen"> | null,
  companyDefault?: FuelUnit | null,
): FuelUnit {
  if (isLegacySilentFuelUnit(aircraft)) return DEFAULT_FUEL_UNIT;
  if (aircraft?.fuelUnit && isFuelUnit(aircraft.fuelUnit)) return aircraft.fuelUnit;
  return hydrateFuelUnit(companyDefault, DEFAULT_FUEL_UNIT);
}

export function lbPerUsGal(fuel: FuelKind = "jeta"): number {
  return fuel === "avgas" ? AVGAS_LB_PER_US_GAL : JET_A_LB_PER_US_GAL;
}

export function roundFuel(amount: number, digits = 4): number {
  if (!Number.isFinite(amount)) return 0;
  const f = 10 ** digits;
  return Math.round(amount * f) / f;
}

function toUsGal(amount: number, unit: FuelUnit, fuel: FuelKind): number {
  const density = lbPerUsGal(fuel);
  switch (unit) {
    case "gal":
      return amount;
    case "L":
      return amount / US_GAL_LITERS;
    case "lb":
      return amount / density;
    case "kg":
      return amount / KG_PER_LB / density;
    default:
      return amount;
  }
}

function fromUsGal(gal: number, unit: FuelUnit, fuel: FuelKind): number {
  const density = lbPerUsGal(fuel);
  switch (unit) {
    case "gal":
      return gal;
    case "L":
      return gal * US_GAL_LITERS;
    case "lb":
      return gal * density;
    case "kg":
      return gal * density * KG_PER_LB;
    default:
      return gal;
  }
}

/** Convert an amount between desk fuel units. Mass↔volume uses the given fuel density. */
export function convertFuel(amount: number, from: FuelUnit, to: FuelUnit, fuel: FuelKind = "jeta"): number {
  if (!Number.isFinite(amount)) return 0;
  if (from === to) return roundFuel(amount);
  if (MASS.has(from) && MASS.has(to)) {
    return from === "kg" ? roundFuel(amount / KG_PER_LB) : roundFuel(amount * KG_PER_LB);
  }
  if (VOLUME.has(from) && VOLUME.has(to)) {
    return from === "gal" ? roundFuel(amount * US_GAL_LITERS) : roundFuel(amount / US_GAL_LITERS);
  }
  return roundFuel(fromUsGal(toUsGal(amount, from, fuel), to, fuel));
}

/** Convert a burn entry to US gallons for catalog `pricePerGal`. */
export function fuelUsedToUsGal(amount: number, unit: FuelUnit, fuel: FuelKind = "jeta"): number {
  return roundFuel(toUsGal(amount, unit, fuel), 4);
}

const WHOLE_FUEL = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  useGrouping: true,
});

/** Nearest whole number. Half away from zero, including negatives. `-0` becomes `0`. */
function wholeFuel(amount: number): number {
  const whole = Math.round(amount);
  return Object.is(whole, -0) ? 0 : whole;
}

/**
 * Fuel figure for every desk and phone surface. Whole kg, lb, US gal, or L,
 * with thousands separators and the unit label. Never a bare number.
 * `digits` is ignored so ledger memos match the same figures.
 */
export function formatFuelAmount(amount: number, unit: FuelUnit, _digits = 1): string {
  return formatFuelFigure(amount, unit);
}

export function formatFuelFigure(amount: number | null | undefined, unit: FuelUnit): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  return `${WHOLE_FUEL.format(wholeFuel(amount))} ${FUEL_UNIT_LABEL[unit]}`;
}

export function formatSignedFuel(amount: number | null | undefined, unit: FuelUnit): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const whole = wholeFuel(amount);
  if (whole > 0) return `+${formatFuelFigure(whole, unit)}`;
  if (whole < 0) return `-${formatFuelFigure(Math.abs(whole), unit)}`;
  return formatFuelFigure(0, unit);
}

export function fuelFieldLabel(kind: "up" | "down" | "used", unit: FuelUnit): string {
  const u = FUEL_UNIT_LABEL[unit];
  if (kind === "up") return `Fuel up (${u})`;
  if (kind === "down") return `Fuel down (${u})`;
  return `Fuel used (${u})`;
}

export function fuelUnitOptions(companyDefault?: FuelUnit): { value: FuelUnit | ""; label: string }[] {
  const inherit = companyDefault ? `Company default (${FUEL_UNIT_LABEL[companyDefault]})` : "Company default";
  return [
    { value: "", label: inherit },
    ...FUEL_UNITS.map((u) => ({ value: u, label: FUEL_UNIT_LABEL[u] })),
  ];
}

export type SimConnectFuelSample = {
  fuelTotalGal?: number | null;
  fuelUsedGal?: number | null;
  fuelStartGal?: number | null;
};

function finiteGal(value: number | null | undefined): number | undefined {
  if (value == null || !Number.isFinite(value) || value < 0) return undefined;
  return roundFuel(value, 1);
}

/**
 * Read SimConnect fuel actuals when a sample carries them.
 * Native desk poll does not request tanks yet — sibling telemetry / HTTP may.
 * Returns null when no actuals exist (SimBrief planned is then the fallback).
 */
export function prefillFuelUsedFromSimConnect(sample?: SimConnectFuelSample | null): {
  fuelUpGal?: number;
  fuelDownGal?: number;
  fuelUsedGal?: number;
} | null {
  if (!sample) return null;
  const down = finiteGal(sample.fuelTotalGal);
  const start = finiteGal(sample.fuelStartGal);
  const used = finiteGal(sample.fuelUsedGal) ?? (start != null && down != null ? finiteGal(start - down) : undefined);
  if (start == null && down == null && used == null) return null;
  return { fuelUpGal: start, fuelDownGal: down, fuelUsedGal: used };
}

/**
 * Prefill fuel used from a SimConnect quantity-weight sample (lb).
 * Returns null when qty is missing — never invent a totalizer.
 */
export function prefillFuelUsedFromSimQty(input?: {
  fuelUsedLb?: number | null;
  unit?: FuelUnit;
}): { amount: number; unit: FuelUnit } | null {
  const lb = input?.fuelUsedLb;
  if (lb == null || !Number.isFinite(lb) || lb <= 0) return null;
  const unit = input?.unit && isFuelUnit(input.unit) ? input.unit : "lb";
  return { amount: convertFuel(lb, "lb", unit), unit };
}
